import React, { useState, useRef, useEffect } from 'react';
import { cn } from './tokens';
import { ChevronDown } from 'lucide-react';

// =============================================================================
// DESCRIPTIVE SELECT
// =============================================================================

export interface DescriptiveOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface DescriptiveSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: DescriptiveOption[];
  placeholder?: string;
  className?: string;
}

/**
 * Custom dropdown that shows label + description per option.
 * Drop-in replacement for Select when options need context.
 */
export function DescriptiveSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  className,
}: DescriptiveSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Scroll active item into view when opening
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [isOpen]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full text-left h-10 px-4 pr-10 rounded-lg border text-sm transition-all duration-200',
          'bg-slate-800 border-slate-700 text-slate-50',
          'hover:border-slate-600',
          'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20',
          'cursor-pointer truncate'
        )}
      >
        {selected?.label || placeholder}
      </button>

      {/* Arrow */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <ChevronDown
          size={16}
          className={cn(
            'text-slate-400 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listRef}
          className={cn(
            'absolute z-50 mt-1 w-full max-h-80 overflow-y-auto',
            'rounded-lg border border-slate-700 bg-slate-900 shadow-xl shadow-black/40',
            'scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent'
          )}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-active={opt.value === value}
              disabled={opt.disabled}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={cn(
                'w-full text-left px-3 py-2 transition-colors',
                'border-b border-slate-800/40 last:border-b-0',
                opt.value === value
                  ? 'bg-amber-900/25 border-l-2 border-l-amber-500'
                  : 'border-l-2 border-l-transparent hover:bg-slate-800/60',
                opt.disabled && 'opacity-40 cursor-not-allowed'
              )}
            >
              <div className="text-sm font-medium text-slate-200 leading-snug">
                {opt.label}
              </div>
              {opt.description && (
                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                  {opt.description}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
